import { ActionIcon, Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

export interface KeyValuePair {
  key: string;
  value: string;
}

export interface KeyValueEditorProps {
  label?: string;
  description?: string;
  pairs: KeyValuePair[];
  onChange: (pairs: KeyValuePair[]) => void;
  /** Placeholder for the key input. */
  keyPlaceholder?: string;
  /** Placeholder for the value input. */
  valuePlaceholder?: string;
  /** Label for the add button. */
  addLabel?: string;
}

/** Row-based editor for opaque string maps (report_metadata & co.) —
 * two text inputs per entry, add/remove buttons, no JSON in sight. */
export function KeyValueEditor({
  label,
  description,
  pairs,
  onChange,
  keyPlaceholder = 'key',
  valuePlaceholder = 'value',
  addLabel = 'Add entry',
}: KeyValueEditorProps) {
  const update = (index: number, patch: Partial<KeyValuePair>) => {
    onChange(pairs.map((pair, i) => (i === index ? { ...pair, ...patch } : pair)));
  };

  const duplicateKeys = new Set(
    pairs.map((p) => p.key.trim()).filter((key, i, keys) => key && keys.indexOf(key) !== i)
  );

  return (
    <Stack gap={6}>
      {label && (
        <div>
          <Text size="sm" fw={500}>
            {label}
          </Text>
          {description && (
            <Text size="xs" c="dimmed">
              {description}
            </Text>
          )}
        </div>
      )}
      {pairs.map((pair, index) => (
        // Index keys are fine here: rows are only ever edited in place,
        // appended, or removed — never reordered.
        <Group key={index} gap="xs" wrap="nowrap" align="flex-start">
          <TextInput
            placeholder={keyPlaceholder}
            value={pair.key}
            onChange={(e) => update(index, { key: e.currentTarget.value })}
            error={duplicateKeys.has(pair.key.trim()) ? 'Duplicate key' : undefined}
            style={{ flex: '0 0 40%' }}
            aria-label={`${label ?? 'entry'} key ${index + 1}`}
          />
          <TextInput
            placeholder={valuePlaceholder}
            value={pair.value}
            onChange={(e) => update(index, { value: e.currentTarget.value })}
            style={{ flex: 1 }}
            aria-label={`${label ?? 'entry'} value ${index + 1}`}
          />
          <ActionIcon
            variant="subtle"
            color="red"
            onClick={() => onChange(pairs.filter((_, i) => i !== index))}
            aria-label={`Remove ${pair.key.trim() || `entry ${index + 1}`}`}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
      <Button
        size="xs"
        variant="light"
        leftSection={<IconPlus size={14} />}
        onClick={() => onChange([...pairs, { key: '', value: '' }])}
        style={{ alignSelf: 'flex-start' }}
      >
        {addLabel}
      </Button>
    </Stack>
  );
}

/** Object → editor rows (values stringified; report_metadata is an opaque
 * string map, so a number/bool loaded from older JSON becomes its string). */
export function objectToPairs(obj: Record<string, unknown> | null | undefined): KeyValuePair[] {
  return Object.entries(obj ?? {}).map(([key, value]) => ({
    key,
    value: value === null || value === undefined ? '' : String(value),
  }));
}

/** Editor rows → object. Blank keys are dropped; duplicate keys last-wins. */
export function pairsToObject(pairs: KeyValuePair[]): Record<string, string> | null {
  const entries = pairs
    .map((pair) => [pair.key.trim(), pair.value] as const)
    .filter(([key]) => key !== '');
  return entries.length ? Object.fromEntries(entries) : null;
}
